import React, { useMemo, useState } from 'react';

import Text from '~/components/core/TextComponents/Text';
import PencilIcon from '~/components/icons/PencilIcon';
import InlineIconButton from '~/components/InlineIconButton';
import Collapse from '~/components/ui/atoms/Collapse';
import Switch from '~/components/ui/atoms/Switch';

import AgentConfigChip, { AGENT_CHIP_TONE } from '../AgentConfigChip';
import copies from '../copies';
import type { ConductorStageRow } from '../types';

import isStageConfigured from './isStageConfigured';

interface StagesCardProps {
  /** Enabled claim stages in configured order, joined to whatever is authored against each key. */
  stages: ConductorStageRow[];
  onEditStage: (stage: ConductorStageRow) => void;
}

/**
 * The stage list, derived and never authored.
 *
 * Membership and order come from the organization's claim-stage configuration, so there is
 * deliberately no way to add, remove, reorder or rename a stage here — the previous editor
 * did all four, and each of them let the authored list drift from the stages a claim actually
 * moves through.
 *
 * The "Not configured" chip is load-bearing rather than decorative: a stage needs both a goal
 * and instructions to be configured, and one missing either is left out of the stages sent to
 * the conductor, so this chip is what tells an operator that stage is not being sent.
 *
 * Those stages are hidden by default, because on a long claim-stage configuration they are the
 * majority and they bury the ones the conductor is actually given. The header switch brings them
 * back, and the header count says how many are out of sight while it is off — hiding a row must
 * not also hide the fact that it exists.
 */
const StagesCard: React.FC<StagesCardProps> = ({ stages, onEditStage }) => {
  // Open by default, matching the section header above and the sibling Actions card.
  const [isOpen, setIsOpen] = useState(true);
  // A view preference for the session, not part of the scope's saved configuration.
  const [showNotConfigured, setShowNotConfigured] = useState(false);

  const visibleStages: ConductorStageRow[] = useMemo(
    () => (showNotConfigured ? stages : stages.filter((stage) => isStageConfigured(stage.content))),
    [stages, showNotConfigured]
  );

  const isFiltered = visibleStages.length !== stages.length;
  // Nothing to reveal in two states: an organization with no enabled stages at all, and a scope
  // where every stage is already configured.
  const hasNotConfiguredStages = stages.some((stage) => !isStageConfigured(stage.content));

  return (
    <div className="flex flex-col gap-12 rounded-md border border-solid border-slate-500 p-16">
      <div className="flex items-center gap-8">
        {/* A real button rather than a clickable div: keyboard-reachable, and `aria-expanded` states
            the collapse rather than leaving it to the layout. */}
        <button
          type="button"
          onClick={() => setIsOpen((wasOpen) => !wasOpen)}
          aria-expanded={isOpen}
          className="flex flex-1 cursor-pointer items-center gap-8 border-0 bg-transparent p-0 text-left"
        >
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="flex-1">
            {copies.editor.stagesTitle}
          </Text>
          {/* Stays in the header so the count is still readable while collapsed. While the list is
              filtered it names both numbers, so the hidden stages are still accounted for. */}
          <AgentConfigChip
            label={
              isFiltered
                ? copies.editor.stagesCountShown(visibleStages.length, stages.length)
                : copies.editor.stagesCount(stages.length)
            }
            tone={AGENT_CHIP_TONE.NEUTRAL}
          />
        </button>
        {/* Beside the collapse button, not inside it: nested, every flip would also collapse the
            card — the same reason the conductor's own switch sits outside its header button. */}
        <div className="flex shrink-0 items-center gap-4">
          {/* Disabled rather than dropped when there is nothing to reveal, which is how the
              sibling expert dialog handles an option that cannot apply: a control that vanishes
              cannot say why, and this one is a filter — its presence is what tells an operator the
              list can be filtered at all. Keeping it also holds the header still while an operator
              configures the last stage, which is when it would otherwise disappear. */}
          <Switch
            checked={showNotConfigured}
            disabled={!hasNotConfiguredStages}
            onChange={(_event, checked: boolean) => setShowNotConfigured(checked)}
            aria-label={copies.editor.showNotConfiguredSwitchLabel}
          />
          <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {copies.editor.showNotConfigured}
          </Text>
        </div>
      </div>
      {/* Hidden rather than unmounted, like the Actions card: the stage rows are derived from props
          so nothing refetches either way, but remounting would drop scroll position on a long list
          for no gain. */}
      <Collapse in={isOpen}>
        <div className="flex flex-col gap-12">
          <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {copies.editor.stagesHelper}
          </Text>
          {stages.length === 0 ? (
            <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              {copies.editor.stagesEmpty}
            </Text>
          ) : null}
          {/* The filter emptying the list is its own state: the organization does have stages, and
              saying it has none would be false — and would leave the operator no way back. */}
          {stages.length > 0 && visibleStages.length === 0 ? (
            <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              {copies.editor.stagesAllNotConfigured(stages.length)}
            </Text>
          ) : null}
          {visibleStages.length > 0 ? (
            <div className="flex flex-col gap-8">
              {visibleStages.map((stage) => (
                // Keyed on the stage key, not the index: position is not identity any more.
                // The whole row is the click target, not just the pencil; the pencil stays as the
                // affordance that says so.
                <div
                  key={stage.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => onEditStage(stage)}
                  // A `role="button"` owes the keys a native button answers to. Only when the row
                  // itself has focus: Enter on the pencil inside already opens through its own click.
                  onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault();
                      onEditStage(stage);
                    }
                  }}
                  className="flex cursor-pointer items-center gap-12 rounded-md border border-solid border-slate-500 p-16 hover:border-slate-600 hover:bg-slate-100"
                >
                  <div className="flex flex-1 flex-col gap-2">
                    <div className="flex items-center gap-8">
                      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
                        {stage.label}
                      </Text>
                      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                        {stage.key}
                      </Text>
                    </div>
                    {/* The goal doubles as the row's summary, so an unset one says so rather than
                  collapsing the row -- the absence is the thing the operator needs to notice. */}
                    <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                      {stage.content?.goal || copies.editor.stageNoGoal}
                    </Text>
                  </div>
                  <AgentConfigChip
                    label={
                      isStageConfigured(stage.content)
                        ? copies.editor.stageConfigured
                        : copies.editor.stageNotConfigured
                    }
                    tone={isStageConfigured(stage.content) ? AGENT_CHIP_TONE.NEUTRAL : AGENT_CHIP_TONE.ATTENTION}
                  />
                  <InlineIconButton
                    icon={PencilIcon}
                    tooltipTitle={copies.editor.edit}
                    // The row already opens the dialog; stop here so one click is one open.
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                      onEditStage(stage);
                    }}
                  />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </Collapse>
    </div>
  );
};

export default StagesCard;
